import React, { useState, useEffect, useMemo } from 'react'
import { isEqual } from 'lodash'

import { removeTeamMember } from '../../../utilities/api'
import { emailPatternMatch } from '../../../utilities/helpers'

import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'

import { Member } from './Member'
import { AddMemberModal } from './AddMemberModal'
import { DeleteModal } from '../../DeleteModal'
import { InvitationSentModal } from './InvitationSentModal'

function rolesForMemberModal(
  roles,
  members,
  editingMemberUuid,
  matchesAccountOwner,
  matchesHiddenRole
) {
  if (!Array.isArray(roles)) return []

  const visibleRoles = roles.filter((r) => !matchesHiddenRole(r?.name))

  const accountOwnerTakenElsewhere = members.some(
    (m) => matchesAccountOwner(m?.role?.name) && m.uuid !== editingMemberUuid
  )

  if (!accountOwnerTakenElsewhere) return visibleRoles

  return visibleRoles.filter((r) => !matchesAccountOwner(r?.name))
}

export default function Team({
  canManageTeam,
  members,
  updateMembers,
  roles,
  inviteMember,
  emailValid,
  isInviting,
  setIsInviting,
  nameMatchesReservedAccountOwner,
  nameMatchesHiddenOrganizationRole,
}) {
  // new and existing role so objects match for controlled input fields
  // also for setting the deleted member
  const [member, setMember] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: {
      id: '',
    },
  })

  // save member email for invitation modal because member state is cleared when closing the add modal
  const [inviteEmail, setInviteEmail] = useState('')

  // set member id (uuid) as flag for editing member
  const [id, setId] = useState()

  const [initialEditState, setInitialEditState] = useState()

  const [showAdd, setShowAdd] = useState(false)

  const [showInviteSent, setShowInviteSent] = useState(false)

  const [inviteSentModalSending, setInviteSentModalSending] = useState(false)

  const [showDelete, setShowDelete] = useState(false)

  const [isEmailValid, setIsEmailValid] = useState(true)

  const [emailErrorMsg, setEmailErrorMsg] = useState('')

  const [isRemoving, setIsRemoving] = useState(false)

  const visibleMembers = useMemo(
    () =>
      members.filter(
        (member) => !nameMatchesHiddenOrganizationRole(member?.role?.name)
      ),
    [members, nameMatchesHiddenOrganizationRole]
  )

  useEffect(() => {
    if (id) setInitialEditState(member)
  }, [id])

  useEffect(() => {
    setIsEmailValid(true)
    setEmailErrorMsg('')
  }, [member?.email])

  const handleMember = (e) => {
    e.target && setMember({ ...member, [e.target.name]: e.target.value })
  }

  const handleRole = (e) => {
    const { value } = e.target
    setMember((prevState) => ({
      ...prevState,
      role: {
        ...prevState.role,
        id: Number(value),
      },
    }))
  }

  const handleAdd = (e) => {
    e.preventDefault()
    if (isEqual(initialEditState, member)) return

    // create new member
    if (!id) {
      // check if email is in use
      if (emailPatternMatch(member?.email)) {
        setIsInviting(true)
        emailValid({ email: member?.email })
          .then((res) => {
            inviteTeamMember(member)
          })
          .catch((err) => {
            setIsEmailValid(false)
            setEmailErrorMsg('Email is already in use by another user')
            setIsInviting(false)
          })
      }
      // editing member
    } else {
      setIsInviting(true)
      inviteTeamMember(member)
    }
  }

  const inviteTeamMember = (member) => {
    const { firstName, lastName, email, uuid } = member
    const { id } = member?.role

    inviteMember({ firstName, lastName, email, role: id, uuid }).then(() => {
      if (!uuid) handleShowInviteSent(email)
      handleCloseAdd()
    })
  }

  const handleResend = (member) => {
    const { firstName, lastName, email } = member
    const { id } = member.role
    setInviteEmail(email)
    setIsInviting(true)
    setInviteSentModalSending(true)
    setShowInviteSent(true)
    inviteMember({ firstName, lastName, email, role: id })
      .then(() => {
        setInviteSentModalSending(false)
      })
      .catch(() => {
        setShowInviteSent(false)
        setInviteEmail('')
        setInviteSentModalSending(false)
        setIsInviting(false)
      })
  }
  // handle add and edit
  const handleShowAdd = (_, member) => {
    setShowAdd(true)
    if (member) {
      setMember(member)
      setId(member?.uuid)
    }
  }

  const handleCloseAdd = () => {
    setShowAdd(false)
    // reset state
    setMember('')
    setId()
    setInitialEditState()
  }

  const handleShowInviteSent = (email) => {
    setInviteEmail(email)
    setInviteSentModalSending(false)
    setShowInviteSent(true)
  }

  const handleCloseInviteSent = () => {
    setShowInviteSent(false)
    setInviteEmail('')
    setInviteSentModalSending(false)
  }

  const handleShowDelete = (member) => {
    setShowDelete(true)
    setMember(member)
  }

  const handleCloseDelete = () => {
    setShowDelete(false)
    setMember('')
  }

  const handleIsEmailValid = (val) => {
    const isMatch = emailPatternMatch(val)
    // if email matches pattern
    if (isMatch) {
      setIsEmailValid(true)
    } else {
      setIsEmailValid(false)
      setEmailErrorMsg('Invalid email. Please enter a valid email address')
    }
  }

  const removeMember = () => {
    setIsRemoving(true)
    removeTeamMember(member)
      .then((res) => {
        let newMembers = visibleMembers.filter((m) => m.email != member?.email)
        updateMembers(newMembers)
        setIsRemoving(false)
        handleCloseDelete()
      })
      .catch((err) => {
        console.error(err)
        setIsRemoving(false)
      })
  }

  return (
    <>
      <div className="heading--flex-space-between mb-4" id="teams">
        <h1 className="fs-md fs-md-bold">Team</h1>
        <Button
          variant="outline-light"
          disabled={!canManageTeam}
          className="btn-plus"
          onClick={handleShowAdd}
        >
          Invite team member
        </Button>
      </div>
      <section data-testid="team-member-list-active">
        <Stack as="ul" gap={2}>
          {visibleMembers
            .filter((member) => !member.pending)
            .map((member, index) => (
              <Member
                key={index}
                canManageTeam={canManageTeam}
                member={member}
                handleShowAdd={handleShowAdd}
                handleShowDelete={handleShowDelete}
                nameMatchesReservedAccountOwner={
                  nameMatchesReservedAccountOwner
                }
              />
            ))}
        </Stack>
      </section>
      <section data-testid="team-member-list-pending">
        <h1 className="text-muted small">Pending invitations</h1>
        <Stack as="ul" gap={2}>
          {visibleMembers
            .filter((member) => member.pending)
            .map((member, index) => (
              <Member
                key={index}
                canManageTeam={canManageTeam}
                member={member}
                handleResend={handleResend}
                handleShowAdd={handleShowAdd}
                handleShowDelete={handleShowDelete}
                nameMatchesReservedAccountOwner={
                  nameMatchesReservedAccountOwner
                }
              />
            ))}
        </Stack>
      </section>

      <AddMemberModal
        show={showAdd}
        handleClose={handleCloseAdd}
        roles={rolesForMemberModal(
          roles,
          visibleMembers,
          id,
          nameMatchesReservedAccountOwner,
          nameMatchesHiddenOrganizationRole
        )}
        id={id}
        member={member}
        handleMember={handleMember}
        handleRole={handleRole}
        handleAdd={handleAdd}
        handleIsEmailValid={handleIsEmailValid}
        isEmailValid={isEmailValid}
        emailErrorMsg={emailErrorMsg}
        isInviting={isInviting}
        isDisabled={
          isEqual(initialEditState, member) ||
          !member?.firstName ||
          !member?.lastName ||
          !member?.email ||
          !member?.role?.id ||
          !isEmailValid ||
          isInviting
        }
      />

      <DeleteModal
        entity="team member"
        show={showDelete}
        handleClose={handleCloseDelete}
        isRemoving={isRemoving}
        handleDelete={removeMember}
      />

      <InvitationSentModal
        show={showInviteSent}
        handleClose={handleCloseInviteSent}
        email={inviteEmail}
        isSending={inviteSentModalSending}
      />
    </>
  )
}
